/**
 * Utility functions for managing shape options when replacing modules
 */

/**
 * List of all shape-related property keys in module options
 */
export const SHAPE_OPTION_KEYS = [
  'shape',
  'shape_type', // legacy
  'shape_radius',
  'shape_stroke_width',
  'shape_stroke_color',
  'shape_stroke_opacity',
  'shape_background_color',
  'shape_background_opacity',
  'shape_pattern',
  'shape_shadow',
  'border',
];

/**
 * Module types that support shape styling
 * - 'asset' - AssetModule
 * - 'upload' - UploadModule
 * - 'shape' - ShapeModule (Shapes)
 * - 'placeholder' - PlaceholderModule (for internal replacement flow)
 */
const SHAPE_SUPPORTING_MODULE_TYPES = [
  'asset',
  'upload',
  'shape',
  'placeholder',
];

/**
 * Check if a module type supports shape styling
 * @param {string} moduleType - The module type to check
 * @returns {boolean} True if module supports shapes
 */
export function moduleSupportsShapes(moduleType) {
  return SHAPE_SUPPORTING_MODULE_TYPES.includes(moduleType);
}

/**
 * Extract shape options from a module's options object
 * @param {Object} options - Module options object
 * @returns {Object} Object containing only shape-related properties
 */
export function extractShapeOptions(options) {
  if (!options) return {};

  const shapeOptions = {};

  SHAPE_OPTION_KEYS.forEach((key) => {
    if (options[key] !== undefined) {
      shapeOptions[key] = options[key];
    }
  });

  return shapeOptions;
}

/**
 * Remove shape options from a module's options object
 * @param {Object} options - Module options object
 * @returns {Object} New options object with shape properties removed
 */
export function removeShapeOptions(options) {
  if (!options) return {};

  const cleanOptions = { ...options };

  SHAPE_OPTION_KEYS.forEach((key) => {
    delete cleanOptions[key];
  });

  return cleanOptions;
}

/**
 * Merge shape options from source to target module
 * Only includes shape properties that exist in the source
 * @param {Object} targetOptions - Target module's options
 * @param {Object} sourceOptions - Source module's options (e.g., moduleToReplace)
 * @returns {Object} New options object with shape properties merged
 */
export function mergeShapeOptions(targetOptions, sourceOptions) {
  const shapeOptions = extractShapeOptions(sourceOptions);
  return {
    ...targetOptions,
    ...shapeOptions,
  };
}

/**
 * Prepare options for module replacement, handling shape properties appropriately
 * Maintains the original spread order: moduleToReplace.options first, then mod.options,
 * but filters shape properties based on whether the new module supports them.
 *
 * Special case: When replacing with a ShapeModule, preserve all shape properties
 * EXCEPT the 'shape' field itself, which should come from the newly selected shape.
 *
 * @param {Object} newModule - The new module being added
 * @param {Object} moduleToReplace - The module being replaced
 * @returns {Object} Merged options with shape properties handled correctly
 */
export function prepareReplacementOptions(newModule, moduleToReplace) {
  if (!moduleToReplace?.options) {
    return newModule.options;
  }

  const newModuleType = newModule.type;
  const newModuleSupportsShapes = moduleSupportsShapes(newModuleType);

  if (newModuleSupportsShapes) {
    // New module supports shapes - preserve shape options from moduleToReplace
    // Order: moduleToReplace base options, then new module defaults, then shape options preserved
    const shapeOptions = extractShapeOptions(moduleToReplace.options);

    // Special case: If replacing with a ShapeModule and it has a specific shape value,
    // don't override the new shape with the old one
    if (newModuleType === 'shape' && newModule.options?.shape !== undefined) {
      // Preserve all shape options except 'shape' itself
      const { shape: _omitShape, ...shapeOptionsWithoutShape } = shapeOptions;
      return {
        ...moduleToReplace.options,
        ...newModule.options,
        ...shapeOptionsWithoutShape, // Preserve border, radius, etc. but not shape type
      };
    }

    return {
      ...moduleToReplace.options,
      ...newModule.options,
      ...shapeOptions, // Ensure shape options are preserved even if newModule has defaults
    };
  }

  // New module doesn't support shapes - strip shape options from moduleToReplace before merging
  const cleanReplacementOptions = removeShapeOptions(moduleToReplace.options);
  return {
    ...cleanReplacementOptions,
    ...newModule.options,
  };
}
